/*
 *
 *  * Copyright(c) 2022 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { SearchFilterCriteriaService } from '@app/ui/search/search-filter-criteria.service';
import { SearchParamKeys, SearchParams } from '@app/common/types/search-params';
import { TranslateService } from '@ngx-translate/core';
import { NotifyService } from '@app/common/notify.service';
import { CustomWorkListService } from '@app/ui/common/work-list-navigator/custom-work-list.service';
import { MessageService } from '@core/lib/services';
import { appConstants } from '@app/common/app-constants';
import { WorkListHeaderDataService } from '@app/ui/common/work-list-header-data.service';
import { Subscription } from 'rxjs';
import { SearchParamsError } from '@app/common/types/search-params-error';
import { AdvanceSearchFilters } from './advance-search-filters';

@Injectable({
  providedIn: 'root'
})
export class AdvanceSearchService {
  private criteria: any = {};
  private $advanceSearchParams: SearchParams;
  private searchParamsError: SearchParamsError = {};

  constructor(
    private translateService: TranslateService,
    private notify: NotifyService,
    private messageService: MessageService,
    private searchFilterCriteriaService: SearchFilterCriteriaService,
    private customWorkListService: CustomWorkListService,
    private workListHeaderDataService: WorkListHeaderDataService
  ) {
    const subscription: Subscription = this.messageService.on('preloadSearchCriteria.done').subscribe(() => {
      this.getFilters();
      subscription.unsubscribe();
    });
  }

  sortByNameProperty = (a, b) => ((a || {}).name || '').localeCompare((b || {}).name || '');

  private loadFilters(): AdvanceSearchFilters {
    const radiologists = this.searchFilterCriteriaService.getRadiologists()?.sort(this.sortByNameProperty);
    const advanceSearchFilters = new AdvanceSearchFilters();
    advanceSearchFilters.descriptions = [];
    advanceSearchFilters.modality = this.searchFilterCriteriaService.getModalities()?.sort(this.sortByNameProperty);
    advanceSearchFilters.bodyParts = this.searchFilterCriteriaService.getBodyParts()?.sort(this.sortByNameProperty);
    advanceSearchFilters.specialties = this.searchFilterCriteriaService
      .getExamSpecialties()
      ?.sort(this.sortByNameProperty);
    advanceSearchFilters.departmentIds = this.searchFilterCriteriaService
      .getDepartments()
      ?.sort(this.sortByNameProperty);
    advanceSearchFilters.examStatusIds = _.each(this.searchFilterCriteriaService.getExamStatuses(), status => {
      status.name = status.examStatusDescription;
    })?.sort(this.sortByNameProperty);
    advanceSearchFilters.protocolStatus = _.each(this.searchFilterCriteriaService.getProtocolStatuses(), status => {
      status.name = status.protocolStatusDescription;
    });
    advanceSearchFilters.initialInterpreters = radiologists;
    advanceSearchFilters.radiologists = radiologists;
    advanceSearchFilters.designatedAuthor = radiologists;
    advanceSearchFilters.referringPhysician = this.searchFilterCriteriaService
      .getReferringPhysicians()
      ?.sort(this.sortByNameProperty);
    advanceSearchFilters.instituteList = _.each(this.searchFilterCriteriaService.getInstitutes(), item => {
      item.name = item.instituteName;
    })?.sort((a, b) => a.instituteName.localeCompare(b.instituteName));
    advanceSearchFilters.currentLocation = this.searchFilterCriteriaService
      .getPatientLocations()
      .sort(this.sortByNameProperty);
    advanceSearchFilters.orderingLocation = this.searchFilterCriteriaService
      .getOrderingLocations()
      ?.sort(this.sortByNameProperty);
    advanceSearchFilters.reportStatusIds = _.each(this.searchFilterCriteriaService.getReportStatuses(), status => {
      status.name = status.reportStatusDescription;
    })?.sort(this.sortByNameProperty);
    advanceSearchFilters.equipment = this.searchFilterCriteriaService.getEquipment()?.sort(this.sortByNameProperty);
    advanceSearchFilters.site = this.searchFilterCriteriaService.getSites()?.sort(this.sortByNameProperty);
    advanceSearchFilters.aiFinding = this.searchFilterCriteriaService
      .getAiFindings()
      ?.sort((a, b) => a.name.localeCompare(b.name));
    advanceSearchFilters.priority = this.searchFilterCriteriaService
      .getExamPriorities()
      ?.sort((a, b) => a.priorityName.localeCompare(b.priorityName));
    advanceSearchFilters.exceptions = this.searchFilterCriteriaService.getExceptions()?.sort(this.sortByNameProperty);
    return advanceSearchFilters;
  }

  updateSupportedReportStatus(reportStatuses): any {
    if (_.isEmpty(this.criteria)) {
      this.getFilters();
    }
    this.criteria.reportStatusIds = _.each(reportStatuses, status => {
      status.name = status.reportStatusDescription;
    })?.sort(this.sortByNameProperty);
  }

  getFilters() {
    if (_.isEmpty(this.criteria)) {
      this.criteria = this.loadFilters();
    }
    return this.criteria;
  }

  get isFiltersReady() {
    return this.searchFilterCriteriaService.filterCriteriaLoaded;
  }

  getProcedureDescriptions(str, isWildCard) {
    return this.searchFilterCriteriaService.getExamDescriptions(str, isWildCard);
  }

  getSearchParamFromValue(key: string, type: string, values: any) {
    if (type === 'Array') {
      const result = [];
      const filters = this.criteria[key];
      if (values instanceof Array) {
        if (key === 'descriptions') {
          values &&
            values.forEach(value => {
              result.push({
                id: value,
                name: value
              });
            });
          this.criteria.descriptions = result;
        } else if (filters instanceof Array && filters && filters.length) {
          const propName = key === 'currentLocation' ? 'id' : SearchParamKeys[key];
          values &&
            values.forEach(value => {
              const found = filters.find(filter => String(filter[propName]) === String(value));
              if (found) {
                result.push(found);
              }
            });
        }
      }
      return result && result.length ? result : undefined;
    } else if (type === 'string') {
      return values;
    } else if (type === 'number') {
      return Number(values);
    } else if (type === 'boolean') {
      return Boolean(values);
    }
    return values;
  }

  isToDoWorkList(worklistParams) {
    let isToDoWL = false;
    let workItemlistDisplayName = worklistParams.worklistName.toLowerCase();
    workItemlistDisplayName = workItemlistDisplayName.replace(/\s/g, '');
    _.each(appConstants.TODO_WORKLIST, function (todo) {
      if (todo.toLowerCase() === workItemlistDisplayName) {
        isToDoWL = true;
      }
    });
    return isToDoWL;
  }

  saveCustomWorklist(searchParams: SearchParams): Promise<any> {
    return new Promise((resolve, reject) => {
      const worklistParams = searchParams.buildUserDefinedWLParams();
      this.checkForExamStatusInput(worklistParams);
      if (this.isToDoWorkList(worklistParams)) {
        this.notify.error(this.translateService.instant('searchModule.worklistNameConflictErrorMsg'));
        reject(this.translateService.instant('searchModule.worklistNameConflictErrorMsg'));
      } else {
        this.customWorkListService
          .createCustomWorklist(worklistParams)
          .then((worklistData: any) => {
            const newWorklistId = worklistData.worklistId.toString();
            this.messageService.broadcast('refresh.myworklist', { id: newWorklistId });
            // highlight the Worklist accordion
            this.messageService.broadcast('parentId', 'worklists');
            // register for notification when the new custom worklist activates
            const listDisplayUnsubscriber = this.messageService.on('list.display').subscribe(data => {
              if (data.list && data.list.id && data.list.id === newWorklistId) {
                this.notify.success(this.translateService.instant('searchModule.worklistSavedMsg'));
                listDisplayUnsubscriber.unsubscribe();
              }
              resolve(undefined);
            });
          })
          .catch(error => {
            reject(this.displayErrorMessage(error));
          })
          .finally(() => {
            this.$advanceSearchParams = undefined;
          });
      }
    });
  }

  editCustomWorklist(worklist, worklistParams) {
    return new Promise((resolve, reject) => {
      const isToDoWL = this.isToDoWorkList(worklistParams);
      if (isToDoWL) {
        this.notify.error(this.translateService.instant('searchModule.worklistNameConflictErrorMsg'));
      } else {
        this.checkForExamStatusInput(worklistParams);
        this.customWorkListService
          .updateCustomWorklist(worklist.id, worklistParams)
          .then((data: any) => {
            this.messageService.broadcast('refresh.myworklist', { id: data.worklistId });
            this.notify.success(this.translateService.instant('searchModule.worklistSavedMsg'));
            resolve(undefined);
            if (this.workListHeaderDataService.checkFilterNeedToReset() === true) {
              this.workListHeaderDataService.resetFilterParam(data.worklistId);
            }
          })
          .catch(error => {
            reject(this.displayErrorMessage(error));
          })
          .finally(() => {
            this.messageService.broadcast('list.refresh', { worklist: worklist, worklistParams: worklistParams });
          });
      }
    });
  }

  private checkForExamStatusInput(worklistParams) {
    if (!worklistParams.examStatusList) {
      worklistParams.examStatusList = _.pluck(this.searchFilterCriteriaService.getExamStatuses(), 'examStatusNumber');
    }
  }

  displayErrorMessage(error) {
    let msg = this.translateService.instant('searchModule.worklistSaveErrorMsg');
    if (error.status === 409) {
      msg = this.translateService.instant('searchModule.worklistNameConflictErrorMsg');
    }
    this.notify.error(msg);
    return msg;
  }

  get advanceSearchParams(): SearchParams {
    return this.$advanceSearchParams;
  }

  set advanceSearchParams(advanceSearchParams: SearchParams) {
    this.$advanceSearchParams = advanceSearchParams;
  }

  getSearchParamErrors() {
    return this.searchParamsError;
  }
}
